import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import CinematicBackground from "@/components/CinematicBackground";
import ImagePicker from "@/components/ImagePicker";
import { PageHeader } from "@/pages/SceneAnalysis";
import { buildFilterCSS, NEUTRAL_ADJ } from "@/data/cinema";
import { DEMO_SCENE } from "@/constants/images";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";
import { toast } from "@/components/ui/sonner";

const SLIDERS = [
  { key: "intensity", tKey: "sl_intensity", min: 0, max: 100 },
  { key: "exposure", tKey: "sl_exposure", min: -60, max: 60 },
  { key: "contrast", tKey: "sl_contrast", min: -60, max: 80 },
  { key: "temperature", tKey: "sl_temperature", min: -80, max: 80 },
  { key: "saturation", tKey: "sl_saturation", min: -80, max: 80 },
  { key: "shadows", tKey: "sl_shadows", min: -60, max: 60 },
  { key: "highlights", tKey: "sl_highlights", min: -60, max: 60 },
];

export default function Filters() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { FILTERS, FILTER_BY_ID, SCENE_TYPES, SCENE_ANALYSIS } = useCinema();
  const { sharedImage, setSharedImage, activeFilterId, setActiveFilterId, setAdjustments } = useApp();
  const [image, setImage] = useState(sharedImage || DEMO_SCENE);
  const [activeId, setActiveId] = useState(activeFilterId || "dramatic-dark");
  const [adj, setAdj] = useState({ ...NEUTRAL_ADJ, ...FILTER_BY_ID[activeFilterId || "dramatic-dark"].base });
  const [scene, setScene] = useState("drama");
  const [showAfter, setShowAfter] = useState(true);

  const applyPreset = (id) => { setActiveId(id); setAdj({ ...NEUTRAL_ADJ, ...FILTER_BY_ID[id].base }); };

  const css = buildFilterCSS(adj);
  const recommendedId = SCENE_ANALYSIS[scene].filter;
  const rtl = lang === "ar";

  const handleImage = (img) => { setImage(img); setSharedImage(img); };
  const reset = () => { setAdj({ ...NEUTRAL_ADJ, ...FILTER_BY_ID[activeId].base }); toast(t("t_resetDone")); };
  const useOnCamera = () => { setActiveFilterId(activeId); setAdjustments(adj); toast.success(t("t_filterApply")); navigate("/app/camera"); };

  return (
    <div className="relative px-4 pt-7" dir={rtl ? "rtl" : "ltr"} data-testid="filters-page">
      <CinematicBackground />
      <PageHeader title={t("filters_title")} icon="fa-wand-magic-sparkles" />

      {/* المعاينة قبل/بعد */}
      <div className="glass rounded-3xl p-3 mb-3">
        <div className="relative rounded-2xl overflow-hidden bg-black/40 aspect-video">
          <img src={image} alt="preview" className="w-full h-full object-cover transition-all duration-300" style={{ filter: showAfter ? css : "none" }} data-testid="filter-preview" />
          <span className="absolute top-2 right-2 text-[11px] bg-black/60 rounded-full px-2 py-0.5 text-slate-200">{showAfter ? t("after") : t("before")}</span>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="compare-btn" onMouseDown={() => setShowAfter(false)} onMouseUp={() => setShowAfter(true)} onMouseLeave={() => setShowAfter(true)}
            onTouchStart={() => setShowAfter(false)} onTouchEnd={() => setShowAfter(true)}
            className="flex-1 glass rounded-xl py-2.5 text-sm text-slate-200 flex items-center justify-center gap-2">
            <i className="fa-solid fa-eye" /> {t("compareHint")}
          </button>
          <button data-testid="reset-btn" onClick={reset} className="glass gold-border rounded-xl py-2.5 px-4 text-sm text-gold flex items-center gap-2">
            <i className="fa-solid fa-rotate-left" /> {t("resetBtn")}
          </button>
        </div>
        <div className="mt-3"><ImagePicker onImage={handleImage} testIdPrefix="filter" /></div>
      </div>

      {/* الفلتر المقترح للمشهد */}
      <div className="glass glass-blue rounded-2xl p-3 mb-4">
        <p className="text-xs text-slate-300 mb-2 flex items-center gap-2"><i className="fa-solid fa-lightbulb text-ai" /> {t("recommendPrompt")}</p>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {SCENE_TYPES.map((s) => (
            <button key={s.id} data-testid={`filter-scene-${s.id}`} onClick={() => setScene(s.id)}
              className={`shrink-0 text-[11px] rounded-full px-3 py-1.5 border ${scene === s.id ? "text-gold border-gold/60 bg-gold/10" : "text-slate-400 border-white/10"}`}>
              {s.name}
            </button>
          ))}
        </div>
        <button data-testid="apply-recommended" onClick={() => applyPreset(recommendedId)} className="mt-2 w-full text-sm text-ai flex items-center justify-center gap-2 py-2">
          <i className="fa-solid fa-star" /> {t("recommendedFor")} <span className="text-gold font-bold">{FILTER_BY_ID[recommendedId].name}</span>
        </button>
      </div>

      {/* شبكة الفلاتر */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => applyPreset(f.id)}
            className="rounded-2xl overflow-hidden relative border transition"
            style={activeId === f.id ? { borderColor: "#d9b46a", boxShadow: "0 0 14px rgba(217,180,106,0.5)" } : { borderColor: "rgba(255,255,255,0.08)" }}>
            <img src={DEMO_SCENE} alt={f.name} className="w-full h-14 object-cover" style={{ filter: buildFilterCSS({ ...f.base, intensity: 80 }) }} />
            <span className="block text-[9px] py-1 text-slate-200 bg-black/50">{f.name}</span>
          </button>
        ))}
      </div>

      {/* التحكم اليدوي */}
      <div className="glass rounded-3xl p-5 space-y-4 mb-5">
        <h3 className="font-display text-slate-200 flex items-center gap-2"><i className="fa-solid fa-sliders text-gold" /> {t("preciseControl")}</h3>
        {SLIDERS.map((sl) => (
          <div key={sl.key}>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-300">{t(sl.tKey)}</span>
              <span className="text-gold font-bold">{adj[sl.key] ?? 0}</span>
            </div>
            <Slider data-testid={`slider-${sl.key}`} value={[adj[sl.key] ?? 0]} min={sl.min} max={sl.max} step={1}
              onValueChange={(v) => setAdj((a) => ({ ...a, [sl.key]: v[0] }))} />
          </div>
        ))}
      </div>

      <button data-testid="use-on-camera-btn" onClick={useOnCamera} className="btn-gold w-full rounded-2xl py-4 font-display text-lg flex items-center justify-center gap-2">
        <i className="fa-solid fa-vr-cardboard" /> {t("useOnCamera")}
      </button>
    </div>
  );
}
